import { CostCard } from "@/components/dashboard/CostCard";
import { TenderAlerts } from "@/components/dashboard/TenderAlerts";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowUpRight, Mail, Radar } from "lucide-react";
import { ErrorState, LoadingState, PageHeader, Pill } from "@/components/common/States";
import { useFetch } from "@/lib/hooks";
import { CLASSIFICATIONS, STAGES, labelOf } from "@/lib/resources";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";

function Kpi({ label, value, hint, to, testid }) {
  const body = (
    <div className="panel h-full p-5 transition-colors hover:border-primary/40" data-testid={testid}>
      <p className="eyebrow">{label}</p>
      <p className="mt-3 font-display text-3xl font-extrabold tracking-tight" data-testid={`${testid}-value`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}

function Pending({ icon: Icon, title, milestone, items, testid }) {
  return (
    <div className="panel p-5" data-testid={testid}>
      <div className="flex items-center justify-between"><p className="eyebrow">{title}</p><Pill tone="slate">Milestone {milestone}</Pill></div>
      <div className="mt-4 space-y-2">
        {items.map(([l, v]) => <div key={l} className="flex justify-between text-sm"><span className="text-muted-foreground">{l}</span><span className="font-mono">{v}</span></div>)}
      </div>
      <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" />Not yet active — figures will populate from stored records once built.</p>
    </div>
  );
}

function StageChart({ byStage }) {
  const data = STAGES.map(([k, l]) => ({ stage: l, count: byStage[k] || 0 }));
  return (
    <div className="h-56" data-testid="pipeline-stage-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: -20 }}>
          <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-25} textAnchor="end" height={50} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <Tooltip cursor={{ fill: "hsl(var(--accent))" }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
          <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Dashboard() {
  const { org } = useWorkspace();
  const { data, loading, error, reload } = useFetch("/dashboard");
  if (loading && !data) return <LoadingState label="Loading dashboard…" />;
  if (error) return <ErrorState message={error} onRetry={reload} testid="dashboard-error" />;
  const c = data.counts;
  const pipeline = data.pipeline_value.length ? data.pipeline_value.map((p) => fmtMoney(p.value, p.currency)).join(" · ") : fmtMoney(0);

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={org?.is_demo ? "Demo workspace · synthetic figures" : "Workspace overview"} title="Dashboard"
        description="Every figure below is computed live from records stored in this organisation." />
      <TenderAlerts />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
        <Kpi label="Open pipeline" value={pipeline} hint={`${c.opportunities} ${c.opportunities === 1 ? "opportunity" : "opportunities"}`} to="/opportunities" testid="kpi-pipeline" />
        <Kpi label="Qualified opportunities" value={c.qualified_opportunities} hint="Qualified → negotiation" to="/opportunities" testid="kpi-qualified" />
        <Kpi label="Strategic prospects" value={c.strategic_prospects} to="/opportunities" testid="kpi-strategic" />
        <Kpi label="Open tasks" value={c.open_tasks} to="/tasks" testid="kpi-tasks" />
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3 lg:gap-6">
        <div className="panel p-5 lg:col-span-2">
          <p className="eyebrow">Pipeline by stage</p>
          <StageChart byStage={data.by_stage} />
        </div>
        <div className="panel p-5" data-testid="classification-breakdown">
          <p className="eyebrow mb-4">Commercial classification</p>
          <div className="space-y-3">
            {CLASSIFICATIONS.map(([k, l]) => (
              <div key={k} className="flex items-center justify-between text-sm"><Pill value={k}>{l}</Pill><span className="font-mono">{data.by_classification[k]}</span></div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3 lg:gap-6">
        <div className="panel p-5" data-testid="upcoming-tasks">
          <div className="mb-4 flex items-center justify-between"><p className="eyebrow">Upcoming tasks</p><Link to="/tasks" className="text-xs text-primary hover:underline">All tasks</Link></div>
          {data.upcoming_tasks.length === 0 ? <p className="text-sm text-muted-foreground">No open tasks.</p> : data.upcoming_tasks.map((t) => (
            <div key={t.id} className="flex items-center justify-between border-b py-2.5 text-sm last:border-0">
              <span className="truncate pr-2">{t.title}</span><span className="shrink-0 font-mono text-xs text-muted-foreground">{fmtDate(t.due_date)}</span>
            </div>
          ))}
        </div>
        <div className="panel p-5" data-testid="research-status-card">
          <div className="flex items-center justify-between"><p className="eyebrow">Research findings & quality</p><Link to="/research" className="text-xs text-primary hover:underline">Open</Link></div>
          <div className="mt-4 space-y-2">
            {[["Campaigns", data.research.campaigns], ["Jobs queued / running", data.research.jobs_running], ["Jobs failed", data.research.jobs_failed],
              ["New evidence (7 days)", data.research.new_findings],
              ["Verified evidence", data.research.evidence_by_status.verified || 0],
              ["Verified / corroborated claims", data.research.verified_claim_ratio == null ? "—" : `${Math.round(data.research.verified_claim_ratio * 100)}%`]].map(([l, v]) => (
              <div key={l} className="flex justify-between text-sm"><span className="text-muted-foreground">{l}</span><span className="font-mono">{v}</span></div>
            ))}
          </div>
          <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground"><Radar className="h-3.5 w-3.5" />Automated discovery activates with Milestone 3 connectors.</p>
        </div>
        <div className="panel p-5" data-testid="email-status-card">
          <div className="flex items-center justify-between"><p className="eyebrow">Outreach</p><Link to="/email" className="text-xs text-primary hover:underline" data-testid="email-status-link">Open Email</Link></div>
          <div className="mt-4 space-y-2">
            {[["Drafts awaiting approval", data.email.drafts_awaiting_approval], ["Ready to send", data.email.ready_to_send], ["Sent this month", data.email.sent_this_month]].map(([l, v]) => (
              <div key={l} className="flex justify-between text-sm"><span className="text-muted-foreground">{l}</span><span className="font-mono">{v ?? 0}</span></div>))}
          </div>
          <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground"><Mail className="h-3.5 w-3.5" />Every email is approved before it's sent from your own mailbox.</p>
        </div>
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3 lg:gap-6">
        <div className="panel p-5 lg:col-span-2" data-testid="recent-activity">
          <p className="eyebrow mb-4">Recent activity</p>
          {data.recent_activity.length === 0 ? <p className="text-sm text-muted-foreground">No activity recorded yet.</p> : data.recent_activity.map((a) => (
            <div key={a.id} className="flex items-start gap-3 border-b py-2.5 text-sm last:border-0">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span className="flex-1"><span className="font-medium capitalize">{a.action.replace("_", " ")}</span> {a.entity_type}{a.label ? ` · ${a.label}` : ""}<span className="block text-xs text-muted-foreground">{a.actor_email} · {fmtDateTime(a.created_at)}</span></span>
            </div>
          ))}
        </div>
        <CostCard />
      </div>
    </div>
  );
}
